"use client";

import { useEffect, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";

import SuccessCard from "@/app/(internal)/auth/reset-password/_components/success-card";
import {
  type ForgotPasswordSchema,
  forgotPasswordSchema,
} from "@/app/(internal)/auth/reset-password/_schemas/forgot-password.schema";
import Button from "@/components/flowbite/button/button";
import Input from "@/components/flowbite/forms/fields/input/input";
import FormCard from "@/components/flowbite/forms/form-card";
import FormTitle from "@/components/flowbite/forms/form-title";
import FormWrapper from "@/components/flowbite/forms/form-wrapper";
import { dictionaries } from "@/dictionaries/content";
import { useRequestPasswordResetMutation } from "@/providers/store/api/auth/auth";
import hasErrors from "@/shared/helpers/has-errors.helper";


export default function Page() {
  const [success, setSuccess] = useState(false);
  const [requestPasswordReset, { isLoading, isSuccess }] = useRequestPasswordResetMutation();

  const {
    control, handleSubmit, formState: { errors }, clearErrors, watch,
  } = useForm<ForgotPasswordSchema>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = (data: { email: string }) => {
    clearErrors();
    void requestPasswordReset(data).unwrap();
  };

  const email = watch("email");

  useEffect(() => {
    if (isSuccess) {
      setSuccess(true);
    }
  }, [isSuccess]);

  if (success) {
    return (
      <SuccessCard />
    );
  }

  return (
    <FormCard classNames="w-[424px] flex flex-col gap-[34px]">
      <FormTitle subTitle={dictionaries.forgotPassword.subTitle}>{dictionaries.forgotPassword.title}</FormTitle>

      <FormWrapper onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-4">
          <Controller
            control={control}
            name="email"
            render={({ field }) => (
              <Input
                {...field}
                error={!!errors.email?.message}
                errorMessage={errors.email?.message}
                label={dictionaries.forgotPassword.email.label}
                placeholder={dictionaries.forgotPassword.email.placeholder}
                type="email"
              />
            )}
          />

          <Button
            className="w-full"
            disabled={hasErrors(errors) || !email || isLoading}
            type="submit"
          >{dictionaries.forgotPassword.sendBtn}</Button>
        </div>
      </FormWrapper>

    </FormCard>
  );
}
